/**
 * Iter 200c · Activity Master — Excel-style inline-editable grid.
 *
 * Renders ALL activities as a scrollable table with inline editing on:
 *   • Name (text)
 *   • Category (select)
 *   • Scope (external / internal)
 *   • Effort/unit (number)
 *   • Rate/unit (number)
 *   • Per-designation hours (dynamic — one column per unique designation
 *     used across activities in the workspace + any pinned "extra" designations
 *     the user chooses to add).
 *   • Per-designation hourly rate (from the workspace rate card) — editable in
 *     a header cell so a single change updates cost across ALL activities that
 *     use that designation.
 *
 * Autosaves on blur (debounced per field). Cost/unit column is computed on the
 * server; grid recomputes local previews so the user sees the impact instantly.
 */
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Save, X, Info, RefreshCw, DollarSign, Clock, ArrowUp, ArrowDown } from "lucide-react";

const CATEGORIES = ["social", "seo", "ads", "content", "web", "email",
  "meeting", "documentation", "adhoc", "other"];

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (!v) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// One-shot per-key debounce so we only send the last edit
function useDebouncedCallback(fn, delay = 500) {
  const timers = useRef({});
  return useCallback((key, ...args) => {
    if (timers.current[key]) clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => {
      delete timers.current[key];
      fn(...args);
    }, delay);
  }, [fn, delay]);
}

export default function ActivityMasterGrid({ wsId, onOpenAdvanced }) {
  const [rows, setRows] = useState(null);
  const [rates, setRates] = useState({});
  const [designations, setDesignations] = useState([]);
  const [extraCols, setExtraCols] = useState([]); // pinned designation columns
  // Iter · Feb 2026 — Workspace-defined content categories (from Content
  // Calendar's "Manage categories" dialog) so custom categories show up in
  // the Activity Master category dropdown too. Previously the list was
  // hardcoded to CATEGORIES only, hiding any category the user added.
  const [wsCategories, setWsCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingIds, setSavingIds] = useState(new Set());
  // Iter 253g · Search + sort + history
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [historyOpen, setHistoryOpen] = useState(null);   // activity row
  const [historyRows, setHistoryRows] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [linkedPackages, setLinkedPackages] = useState([]);

  const load = useCallback(async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      const [act, r, dl, cats] = await Promise.all([
        api.get(`/workspaces/${wsId}/activity-types?include_inactive=false`),
        api.get(`/workspaces/${wsId}/designation-rates`),
        api.get(`/workspaces/${wsId}/designations`),
        api.get(`/workspaces/${wsId}/content-categories`).catch(() => ({ data: [] })),
      ]);
      setRows(act.data || []);
      setRates(r.data?.rates || {});
      setDesignations(dl.data || []);
      setWsCategories(cats.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load activity master"));
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  // Union of all skills used across activities + extra pinned + all
  // workspace designations (from the rate card) so users see every eligible
  // column even when no activity has used it yet — R92 fix.
  const skillCols = useMemo(() => {
    const s = new Set();
    for (const r of rows || []) {
      for (const sk of (r.skill_breakdown || [])) {
        if (sk.designation) s.add(sk.designation);
      }
    }
    for (const d of designations) if (d) s.add(d);
    for (const d of extraCols) if (d) s.add(d);
    return Array.from(s).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [rows, designations, extraCols]);

  const addableDesignations = useMemo(
    () => designations.filter((d) => !skillCols.includes(d)),
    [designations, skillCols]
  );

  // Merge hardcoded + workspace-custom categories, dedupe case-insensitively
  // and keep insertion order (built-ins first, then custom, alphabetised).
  // Each entry is {name, color?}. Built-ins have no colour so they render
  // with a neutral dot.
  const allCategories = useMemo(() => {
    const seen = new Map(); // lower → {name, color}
    for (const c of CATEGORIES) seen.set(c.toLowerCase(), { name: c, color: null });
    const custom = [...(wsCategories || [])].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""));
    for (const c of custom) {
      if (!c?.name) continue;
      const k = c.name.toLowerCase();
      if (!seen.has(k)) seen.set(k, { name: c.name, color: c.color || null });
      else seen.set(k, { name: seen.get(k).name, color: c.color || seen.get(k).color });
    }
    return [...seen.values()];
  }, [wsCategories]);

  // Iter 253g · Filter + sort helpers
  const displayRows = useMemo(() => {
    let list = rows || [];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.category || "").toLowerCase().includes(q));
    }
    if (sortField) {
      const sign = sortDir === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const av = a[sortField]; const bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return sign * (av - bv);
        return sign * String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
      });
    }
    return list;
  }, [rows, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField !== field) { setSortField(field); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortField(null); setSortDir("asc"); }
  };

  const openHistory = async (row) => {
    setHistoryOpen(row); setHistoryBusy(true); setHistoryRows([]); setLinkedPackages([]);
    try {
      const [h, p] = await Promise.all([
        api.get(`/activity-types/${row.id}/history`),
        api.get(`/activity-types/${row.id}/linked-packages`),
      ]);
      setHistoryRows(h.data || []);
      setLinkedPackages(p.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load history"));
    } finally { setHistoryBusy(false); }
  };

  // ─── Patch helpers ─────────────────────────────────────────────────

  const markSaving = (id, on) => {
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };

  const patchActivity = async (id, patch) => {
    markSaving(id, true);
    try {
      const { data } = await api.patch(`/activity-types/${id}`, patch);
      // Iter · Feb 2026 — Backend attaches `_cascade` when category
      // changes ripple to package lines + content items; surface it so
      // the user sees the reach of their edit.
      if (data?._cascade) {
        const c = data._cascade;
        const parts = [];
        if (c.package_lines) parts.push(`${c.package_lines} package line${c.package_lines === 1 ? "" : "s"}`);
        if (c.content_items) parts.push(`${c.content_items} calendar deliverable${c.content_items === 1 ? "" : "s"}`);
        if (parts.length) {
          toast.success(`Category cascaded to ${parts.join(" & ")}.`,
                        { description: c.packages_touched
                          ? `${c.packages_touched} package${c.packages_touched === 1 ? "" : "s"} touched.`
                          : undefined });
        }
      }
      const { _cascade, ...clean } = data || {};
      setRows((prev) => (prev || []).map((r) => r.id === id ? { ...r, ...clean } : r));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
      // Roll back by reloading
      load();
    } finally {
      markSaving(id, false);
    }
  };
  const debouncedPatch = useDebouncedCallback(patchActivity, 450);

  const setField = (id, field, value) => {
    setRows((prev) => (prev || []).map((r) => r.id === id ? { ...r, [field]: value } : r));
    debouncedPatch(`${id}:${field}`, id, { [field]: value });
  };

  // We want to send skill_breakdown after debouncing across per-designation edits.
  // Uses a per-row flush that reads the latest local state from `rowsRef`.
  const skillTimers = useRef({});
  const rowsRef = useRef(null);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const scheduleSkillFlush = (id) => {
    if (skillTimers.current[id]) clearTimeout(skillTimers.current[id]);
    skillTimers.current[id] = setTimeout(() => {
      delete skillTimers.current[id];
      const row = (rowsRef.current || []).find((r) => r.id === id);
      if (!row) return;
      const payload = (row.skill_breakdown || [])
        .filter((s) => s.designation && (s.hours || 0) > 0)
        .map((s) => ({ designation: s.designation, hours: Number(s.hours) || 0 }));
      // Iter 207b — when a skill breakdown is present, snap the activity's
      // default_effort_hours to the SUM so Effort/U cannot drift.
      const patch = { skill_breakdown: payload };
      if (payload.length > 0) {
        patch.default_effort_hours = payload.reduce((s, sk) => s + (Number(sk.hours) || 0), 0);
      }
      patchActivity(id, patch);
    }, 500);
  };

  const setSkillHoursV2 = (id, designation, hours) => {
    setRows((prev) => (prev || []).map((r) => {
      if (r.id !== id) return r;
      const list = [...(r.skill_breakdown || [])];
      const idx = list.findIndex((s) => s.designation === designation);
      const numH = hours === "" || hours == null ? 0 : Number(hours);
      if (idx >= 0) {
        if (numH === 0) list.splice(idx, 1);
        else list[idx] = { designation, hours: numH };
      } else if (numH > 0) {
        list.push({ designation, hours: numH });
      }
      return { ...r, skill_breakdown: list };
    }));
    scheduleSkillFlush(id);
  };

  const removeSkillCol = (designation) => {
    if (!window.confirm(`Remove "${designation}" column? Any hours entered for this designation across activities will be cleared.`)) return;
    // Remove from all rows
    const impacted = (rows || []).filter((r) => (r.skill_breakdown || []).some((s) => s.designation === designation));
    setRows((prev) => (prev || []).map((r) => ({
      ...r,
      skill_breakdown: (r.skill_breakdown || []).filter((s) => s.designation !== designation),
    })));
    setExtraCols((prev) => prev.filter((d) => d !== designation));
    Promise.all(impacted.map((r) =>
      api.patch(`/activity-types/${r.id}`, {
        skill_breakdown: (r.skill_breakdown || [])
          .filter((s) => s.designation !== designation)
          .map((s) => ({ designation: s.designation, hours: Number(s.hours) || 0 })),
      })
    )).then(() => {
      toast.success(`Column "${designation}" cleared from ${impacted.length} activit${impacted.length === 1 ? "y" : "ies"}`);
    }).catch(() => {
      toast.error("Some rows failed — reloading");
      load();
    });
  };

  const addSkillCol = (designation) => {
    if (!designation || skillCols.includes(designation)) return;
    setExtraCols((prev) => [...prev, designation]);
  };

  // ─── Rate card editing ────────────────────────────────────────────

  const rateTimer = useRef(null);
  const [pendingRates, setPendingRates] = useState(null);
  const currentRates = pendingRates || rates;
  const setRate = (designation, val) => {
    const next = { ...currentRates, [designation]: val === "" ? 0 : Number(val) };
    setPendingRates(next);
    if (rateTimer.current) clearTimeout(rateTimer.current);
    rateTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/workspaces/${wsId}/designation-rates`, { rates: next });
        setRates(data.rates || next);
        setPendingRates(null);
        // Reload activities so default_cost recomputes — but skip if any
        // per-row edits are still in flight (they'd otherwise be stomped).
        if (savingIds.size === 0) {
          const act = await api.get(`/workspaces/${wsId}/activity-types?include_inactive=false`);
          setRows(act.data || []);
        }
      } catch (e) {
        toast.error(apiErrorMessage(e, "Rate save failed"));
        setPendingRates(null);
      }
    }, 600);
  };

  // ─── Add / delete row ─────────────────────────────────────────────

  const addRow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/activity-types`, {
        name: "New activity",
        category: "other",
        is_external: true,
        default_effort_hours: 0,
        default_rate: 0,
        skill_breakdown: [],
      });
      setRows((prev) => [data, ...(prev || [])]);
      toast.success("Row added — click Name to rename");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Add failed"));
    } finally { setBusy(false); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"?`)) return;
    try {
      await api.delete(`/activity-types/${row.id}`);
      setRows((prev) => (prev || []).filter((r) => r.id !== row.id));
      toast.success("Deactivated");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    }
  };

  // ─── Render ────────────────────────────────────────────────────────

  if (!rows) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="grid-loading">Loading activity master…</div>;
  }

  const totalSkillCol = skillCols.length;

  return (
    <div className="space-y-3" data-testid="activity-master-grid">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Info className="w-3.5 h-3.5" />
          <span>Excel-like grid — click headers to sort, click any cell to edit.</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search activity / category…"
            className="h-8 w-56 px-2 text-xs border border-border bg-background focus:outline-none focus:border-primary"
            data-testid="activity-master-search"
          />
          <Button size="sm" variant="outline" className="rounded-none"
                  onClick={() => {
                    if (!displayRows.length) { toast.info("Nothing to export"); return; }
                    const headers = ["Activity", "Category", "Scope", "Effort/u (hrs)", "Rate/u", "Cost/u", "Margin", "Packages"];
                    const skillHeaders = skillCols.map((s) => `${s} (hrs)`);
                    const allHeaders = [...headers, ...skillHeaders];
                    const rowsCsv = displayRows.map((r) => {
                      const cost = (r.skill_breakdown || [])
                        .reduce((s, sk) => s + (Number(sk.hours) || 0) * (Number(currentRates[sk.designation]) || 0), 0);
                      const rate = Number(r.default_rate) || 0;
                      const margin = rate - cost;
                      const skills = skillCols.map((s) => {
                        const h = (r.skill_breakdown || []).find((x) => x.designation === s)?.hours;
                        return h ?? "";
                      });
                      return [
                        r.name || "", r.category || "", r.is_external ? "external" : "internal",
                        r.default_effort_hours ?? "", rate.toFixed(2), cost.toFixed(2), margin.toFixed(2),
                        r.package_count ?? 0, ...skills,
                      ];
                    });
                    const csv = [allHeaders, ...rowsCsv]
                      .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
                      .join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `activity-master-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(`Exported ${displayRows.length} rows`);
                  }}
                  data-testid="activity-master-export-csv">
            Export CSV
          </Button>
          <AddSkillDropdown
            options={addableDesignations}
            onAdd={addSkillCol}
          />
          <Button size="sm" variant="outline" className="rounded-none"
                  onClick={load} disabled={busy}
                  data-testid="grid-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" className="rounded-none" onClick={addRow} disabled={busy}
                  data-testid="grid-add-row">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="border border-border overflow-auto max-h-[70vh] bg-card" data-testid="grid-scroll">
        <table className="text-xs w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-muted/60 backdrop-blur">
            <tr>
              <SortTh sticky field="name" label="Activity" sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="th-name" />
              <SortTh field="category" label="Category" sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="th-category" />
              <Th>Scope</Th>
              <SortTh field="default_effort_hours" align="right" label={<>Effort/u<br/><span className="text-[9px] font-normal opacity-60">hours</span></>} sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="th-effort" />
              <SortTh field="default_rate" align="right" label={<>Rate/u<br/><span className="text-[9px] font-normal opacity-60">charged</span></>} sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="th-rate" />
              <Th className="text-right bg-emerald-50/50">Cost/u<br/><span className="text-[9px] font-normal opacity-60">auto</span></Th>
              <Th className="text-right bg-emerald-50/50">Margin<br/><span className="text-[9px] font-normal opacity-60">auto</span></Th>
              <SortTh field="package_count" align="right" label={<>Packages<br/><span className="text-[9px] font-normal opacity-60">linked</span></>} sortField={sortField} sortDir={sortDir} onClick={toggleSort} testid="th-packages" />
              {skillCols.map((d) => (
                <Th key={d} className="text-right bg-blue-50/40 border-l border-border" testid={`grid-skill-col-${d}`}>
                  <div className="flex flex-col items-end gap-0.5 min-w-[110px]">
                    <div className="flex items-center gap-1">
                      <span className="truncate max-w-[110px]" title={d}>{d}</span>
                      <button
                        type="button"
                        onClick={() => removeSkillCol(d)}
                        className="opacity-60 hover:opacity-100 hover:text-destructive"
                        data-testid={`grid-remove-skill-${d}`}
                        aria-label={`Remove ${d} column`}
                        title={`Remove ${d} column`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    {/* Editable hourly rate for this designation */}
                    <div className="inline-flex items-center gap-0.5 text-[9px] font-normal">
                      <DollarSign className="w-2.5 h-2.5 opacity-60" />
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={currentRates[d] ?? 0}
                        onChange={(e) => setRate(d, e.target.value)}
                        className="w-16 h-5 px-1 text-[10px] text-right bg-background border border-border rounded-none focus:border-primary focus:outline-none"
                        data-testid={`grid-rate-${d}`}
                        aria-label={`Hourly rate for ${d}`}
                      />
                      <span className="opacity-60">/hr</span>
                    </div>
                  </div>
                </Th>
              ))}
              <Th className="text-center w-10"> </Th>
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 ? (
              <tr><td colSpan={8 + totalSkillCol + 1}
                       className="text-center py-6 text-muted-foreground italic"
                       data-testid="grid-empty">
                {search ? "No activities match your search." : "No activities yet. Click \"Add row\" to create one."}
              </td></tr>
            ) : displayRows.map((r) => (
              <GridRow
                key={r.id}
                row={r}
                skillCols={skillCols}
                rates={currentRates}
                allCategories={allCategories}
                onName={(v) => setField(r.id, "name", v)}
                onCategory={(v) => setField(r.id, "category", v)}
                onScope={(v) => setField(r.id, "is_external", v === "external")}
                onEffort={(v) => setField(r.id, "default_effort_hours", Number(v) || 0)}
                onRate={(v) => setField(r.id, "default_rate", Number(v) || 0)}
                onSkill={(d, h) => setSkillHoursV2(r.id, d, h)}
                onDelete={() => deleteRow(r)}
                onOpenAdvanced={() => onOpenAdvanced?.(r)}
                onOpenHistory={() => openHistory(r)}
                saving={savingIds.has(r.id)}
              />
            ))}
          </tbody>
          {displayRows.length > 0 && (
            <tfoot className="sticky bottom-0 bg-muted/70 backdrop-blur">
              <TotalsRow rows={displayRows} skillCols={skillCols} rates={currentRates} />
            </tfoot>
          )}
        </table>
      </div>

      {historyOpen && (
        <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center" data-testid="activity-history-drawer">
          <div className="bg-background border border-border shadow-lg w-[720px] max-h-[80vh] overflow-y-auto flex flex-col">
            <div className="border-b border-border p-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold">{historyOpen.name}</div>
                <div className="text-[11px] text-muted-foreground">Change history &amp; linked packages</div>
              </div>
              <button type="button" onClick={() => setHistoryOpen(null)}
                       className="text-muted-foreground hover:text-foreground"
                       data-testid="activity-history-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <section>
                <div className="label-eyebrow mb-2">Linked packages ({linkedPackages.length})</div>
                {linkedPackages.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic border border-dashed border-border p-4 text-center" data-testid="linked-packages-empty">Not used in any package yet.</div>
                ) : (
                  <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {linkedPackages.map((p) => (
                      <li key={p.id} className="flex items-center justify-between border-b border-border py-1" data-testid={`linked-pkg-${p.id}`}>
                        <a href={`/app/packages/${p.id}`} className="text-primary hover:underline">{p.name}</a>
                        <span className="text-muted-foreground">{p.status || "—"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section>
                <div className="label-eyebrow mb-2">Change log ({historyRows.length})</div>
                {historyBusy && <div className="text-xs text-muted-foreground">Loading…</div>}
                {!historyBusy && historyRows.length === 0 && (
                  <div className="text-xs text-muted-foreground italic border border-dashed border-border p-4 text-center" data-testid="activity-history-empty">
                    No changes recorded yet. Every edit from now on will be logged.
                  </div>
                )}
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {historyRows.map((h) => (
                    <div key={h.id} className="border border-border p-2 text-xs" data-testid={`activity-history-row-${h.id}`}>
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span><b>{h.actor_name}</b></span>
                        <span>{h.created_at?.slice(0, 16).replace("T", " ")}</span>
                      </div>
                      <table className="w-full text-[11px] mt-1">
                        <tbody>
                          {(h.changes || []).map((c, i) => (
                            <tr key={i} className="border-t border-border">
                              <td className="py-0.5 pr-2 font-mono text-muted-foreground">{c.field}</td>
                              <td className="py-0.5 pr-2 text-muted-foreground line-through max-w-[220px] truncate" title={JSON.stringify(c.before)}>
                                {typeof c.before === "object" ? JSON.stringify(c.before) : String(c.before ?? "—")}
                              </td>
                              <td className="py-0.5 text-primary max-w-[220px] truncate" title={JSON.stringify(c.after)}>
                                → {typeof c.after === "object" ? JSON.stringify(c.after) : String(c.after ?? "—")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, sticky = false, className = "", testid }) {
  return (
    <th
      className={`px-2 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold border-b border-border text-left ${sticky ? "sticky left-0 z-10 bg-muted/80" : ""} ${className}`}
      data-testid={testid}
    >
      {children}
    </th>
  );
}

// Iter 253g · Sortable header cell
function SortTh({ field, label, sticky = false, align = "left", sortField, sortDir, onClick, testid }) {
  const active = sortField === field;
  return (
    <th
      className={`px-2 py-2 text-[10px] uppercase tracking-widest font-semibold border-b border-border cursor-pointer select-none transition-colors ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"} ${sticky ? "sticky left-0 z-10 bg-muted/80" : ""} ${align === "right" ? "text-right" : "text-left"}`}
      onClick={() => onClick(field)}
      data-testid={testid}
    >
      <div className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        <span>{label}</span>
        {active && (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </div>
    </th>
  );
}

function AddSkillDropdown({ options, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = options.filter((o) =>
    o.toLowerCase().includes(q.toLowerCase()));

  if (options.length === 0) {
    return (
      <span className="text-[10px] text-muted-foreground italic" data-testid="grid-add-skill-empty">
        All designations already listed
      </span>
    );
  }

  return (
    <div className="relative">
      <Button size="sm" variant="outline" className="rounded-none"
              onClick={() => setOpen((v) => !v)}
              data-testid="grid-add-skill-btn">
        <Plus className="w-3.5 h-3.5 mr-1" /> Skill column
      </Button>
      {open && (
        <div className="absolute right-0 top-9 z-20 w-56 bg-card border border-border shadow-lg"
             data-testid="grid-add-skill-menu">
          <div className="p-2 border-b border-border">
            <Input
              autoFocus placeholder="Filter…" value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-7 text-xs rounded-none"
              data-testid="grid-add-skill-search"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground italic text-center">No matches</div>
            ) : filtered.map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => { onAdd(o); setOpen(false); setQ(""); }}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted/40"
                data-testid={`grid-add-skill-opt-${o}`}
              >
                {o}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GridRow({ row, skillCols, rates, allCategories, onName, onCategory, onScope,
                    onEffort, onRate, onSkill, onDelete, onOpenAdvanced,
                    onOpenHistory, saving }) {
  const skillHours = useMemo(() => {
    const m = {};
    for (const s of (row.skill_breakdown || [])) m[s.designation] = s.hours;
    return m;
  }, [row.skill_breakdown]);

  // Iter 207b — When a skill breakdown exists, Effort/U becomes the sum of
  // skill hours and is no longer editable. This prevents drift between the
  // effort-per-unit shown to sales and the effort actually rostered across
  // skills for delivery.
  const skillEffortSum = useMemo(() => (row.skill_breakdown || [])
    .reduce((s, sk) => s + (Number(sk.hours) || 0), 0),
  [row.skill_breakdown]);
  const skillsProvided = (row.skill_breakdown || []).length > 0;
  const effortForRow = skillsProvided ? skillEffortSum : (row.default_effort_hours ?? 0);

  const unitCost = useMemo(() => (row.skill_breakdown || [])
    .reduce((sum, s) => sum + (Number(s.hours) || 0) * (Number(rates[s.designation]) || 0), 0),
  [row.skill_breakdown, rates]);
  const margin = (Number(row.default_rate) || 0) - unitCost;
  const marginPct = row.default_rate ? (margin / row.default_rate) * 100 : null;
  const marginCls =
    marginPct == null ? "text-muted-foreground"
      : marginPct >= 40 ? "text-emerald-600"
      : marginPct >= 15 ? "text-amber-600"
      : "text-red-600";

  return (
    <tr className="border-b border-border hover:bg-muted/20" data-testid={`grid-row-${row.id}`}>
      {/* Name — sticky left */}
      <td className="px-2 py-1 sticky left-0 bg-card group-hover:bg-muted/20 border-r border-border"
          style={{ minWidth: 180 }}>
        <div className="flex items-center gap-1">
          <CellInput
            value={row.name || ""}
            onCommit={onName}
            testid={`grid-cell-name-${row.id}`}
            className="font-semibold"
          />
          {saving && (
            <span className="text-[9px] text-primary" data-testid={`grid-saving-${row.id}`}>
              <Save className="w-3 h-3 inline animate-pulse" />
            </span>
          )}
        </div>
      </td>

      {/* Category — shadcn Select shows a saved-colour dot next to
          each option (and next to the selected trigger) to match the
          Advanced Activity dialog. */}
      <td className="px-1 py-1 border-r border-border">
        {(() => {
          const cur = row.category || "other";
          const known = allCategories.some((c) => c.name.toLowerCase() === cur.toLowerCase());
          const curEntry = allCategories.find((c) => c.name.toLowerCase() === cur.toLowerCase());
          return (
            <Select value={cur} onValueChange={onCategory}>
              <SelectTrigger className="h-7 text-xs bg-transparent border border-transparent hover:border-border focus:border-primary px-1 rounded-none focus:outline-none"
                             data-testid={`grid-cell-category-${row.id}`}>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: curEntry?.color || "#94a3b8" }} />
                  <span>{cur}{!known && <span className="text-[9px] text-muted-foreground ml-1">(legacy)</span>}</span>
                </span>
              </SelectTrigger>
              <SelectContent>
                {!known && (
                  <SelectItem value={cur}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#94a3b8" }} />
                      <span>{cur} <span className="text-[9px] text-muted-foreground">(legacy)</span></span>
                    </span>
                  </SelectItem>
                )}
                {allCategories.map((c) => (
                  <SelectItem key={c.name} value={c.name}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: c.color || "#94a3b8" }} />
                      <span>{c.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        })()}
      </td>

      {/* Scope */}
      <td className="px-1 py-1 border-r border-border">
        <select
          value={row.is_external ? "external" : "internal"}
          onChange={(e) => onScope(e.target.value)}
          className="h-7 text-xs bg-transparent border border-transparent hover:border-border focus:border-primary px-1 rounded-none focus:outline-none"
          data-testid={`grid-cell-scope-${row.id}`}
        >
          <option value="external">external</option>
          <option value="internal">internal</option>
        </select>
      </td>

      {/* Effort/unit — auto-calculated (Σ skill hours) when a breakdown exists */}
      <td className={`px-1 py-1 text-right border-r border-border ${skillsProvided ? "bg-amber-50/40" : ""}`}
          title={skillsProvided ? "Auto-calculated from skill breakdown (Σ skill hours)" : "Free-form effort per unit"}
          data-testid={`grid-cell-effort-wrap-${row.id}`}>
        {skillsProvided ? (
          <div className="h-7 flex items-center justify-end px-1 font-mono text-[11px] text-amber-900 tabular-nums"
               data-testid={`grid-cell-effort-auto-${row.id}`}>
            {effortForRow.toFixed(2)}
            <span className="ml-1 text-[9px] opacity-60">auto</span>
          </div>
        ) : (
          <CellNumber
            value={row.default_effort_hours ?? 0}
            onCommit={onEffort}
            step={0.25}
            testid={`grid-cell-effort-${row.id}`}
          />
        )}
      </td>

      {/* Rate/unit */}
      <td className="px-1 py-1 text-right border-r border-border">
        <CellNumber
          value={row.default_rate ?? 0}
          onCommit={onRate}
          step={1}
          testid={`grid-cell-rate-${row.id}`}
        />
      </td>

      {/* Cost/unit — auto */}
      <td className="px-2 py-1 text-right font-mono text-[11px] bg-emerald-50/30 border-r border-border"
          data-testid={`grid-cell-cost-${row.id}`}>
        {fmtMoney(unitCost)}
      </td>

      {/* Margin — auto */}
      <td className={`px-2 py-1 text-right font-mono text-[11px] bg-emerald-50/30 border-r border-border ${marginCls}`}
          data-testid={`grid-cell-margin-${row.id}`}>
        {fmtMoney(margin)}
        {marginPct != null && (
          <span className="ml-1 text-[9px] opacity-70">({Math.round(marginPct)}%)</span>
        )}
      </td>

      {/* Iter 253g · Packages count */}
      <td className="px-2 py-1 text-right border-r border-border" data-testid={`grid-cell-packages-${row.id}`}>
        {row.package_count > 0 ? (
          <button type="button" onClick={onOpenHistory}
                   className="text-primary font-mono text-[11px] underline hover:no-underline"
                   title="View packages using this activity">
            {row.package_count}
          </button>
        ) : (
          <span className="text-muted-foreground font-mono text-[11px]">0</span>
        )}
      </td>

      {/* Per-skill cells */}
      {skillCols.map((d) => (
        <td key={d} className="px-1 py-1 text-right bg-blue-50/20 border-l border-border">
          <CellNumber
            value={skillHours[d] ?? ""}
            placeholder="—"
            onCommit={(v) => onSkill(d, v)}
            step={0.25}
            allowEmpty
            testid={`grid-cell-skill-${row.id}-${d}`}
          />
        </td>
      ))}

      {/* Row actions */}
      <td className="px-1 py-1 text-right border-l border-border">
        <div className="flex items-center gap-1 justify-end">
          {onOpenHistory && (
            <button
              type="button"
              onClick={onOpenHistory}
              className="text-muted-foreground hover:text-primary"
              data-testid={`grid-row-history-${row.id}`}
              aria-label="Change history"
              title="Change history + linked packages"
            >
              <Clock className="w-3.5 h-3.5" />
            </button>
          )}
          {onOpenAdvanced && (
            <button
              type="button"
              onClick={onOpenAdvanced}
              className="text-muted-foreground hover:text-primary text-[10px] underline"
              data-testid={`grid-row-advanced-${row.id}`}
              title="Advanced edit (colour, icon, QC checklist, SOP, description)"
            >
              …
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            className="text-muted-foreground hover:text-destructive"
            data-testid={`grid-row-delete-${row.id}`}
            aria-label="Deactivate"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function TotalsRow({ rows, skillCols, rates }) {
  const totals = useMemo(() => {
    let effort = 0, rate = 0, cost = 0;
    const perSkill = {};
    for (const r of rows) {
      effort += Number(r.default_effort_hours) || 0;
      rate += Number(r.default_rate) || 0;
      const rowCost = (r.skill_breakdown || []).reduce(
        (s, sk) => s + (Number(sk.hours) || 0) * (Number(rates[sk.designation]) || 0), 0);
      cost += rowCost;
      for (const sk of (r.skill_breakdown || [])) {
        perSkill[sk.designation] = (perSkill[sk.designation] || 0) + (Number(sk.hours) || 0);
      }
    }
    return { effort, rate, cost, margin: rate - cost, perSkill };
  }, [rows, skillCols, rates]);

  return (
    <tr className="border-t-2 border-border font-semibold" data-testid="grid-totals-row">
      <td className="px-2 py-1.5 text-[10px] uppercase tracking-widest sticky left-0 bg-muted/70 border-r border-border">
        Totals · {rows.length} row{rows.length === 1 ? "" : "s"}
      </td>
      <td className="border-r border-border" />
      <td className="border-r border-border" />
      <td className="px-2 py-1.5 text-right font-mono border-r border-border" data-testid="grid-total-effort">
        {totals.effort.toFixed(2)}
      </td>
      <td className="px-2 py-1.5 text-right font-mono border-r border-border" data-testid="grid-total-rate">
        {fmtMoney(totals.rate)}
      </td>
      <td className="px-2 py-1.5 text-right font-mono border-r border-border bg-emerald-50/50" data-testid="grid-total-cost">
        {fmtMoney(totals.cost)}
      </td>
      <td className={`px-2 py-1.5 text-right font-mono border-r border-border bg-emerald-50/50 ${totals.margin >= 0 ? "text-emerald-700" : "text-red-600"}`}
          data-testid="grid-total-margin">
        {fmtMoney(totals.margin)}
      </td>
      {skillCols.map((d) => (
        <td key={d} className="px-2 py-1.5 text-right font-mono bg-blue-50/40 border-l border-border"
            data-testid={`grid-total-skill-${d}`}>
          {(totals.perSkill[d] || 0).toFixed(2)}
        </td>
      ))}
      <td className="border-l border-border" />
    </tr>
  );
}

// ─── Cell primitives ─────────────────────────────────────────────────

function CellInput({ value, onCommit, testid, className = "" }) {
  const [v, setV] = useState(value);
  const skipCommit = useRef(false);
  useEffect(() => { setV(value); }, [value]);
  return (
    <input
      value={v ?? ""}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if (skipCommit.current) { skipCommit.current = false; setV(value); return; }
        if (v !== value) onCommit(v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.target.blur(); }
        if (e.key === "Escape") {
          skipCommit.current = true;
          setV(value);
          e.target.blur();
        }
      }}
      className={`w-full h-7 px-1 text-xs bg-transparent border border-transparent hover:border-border focus:border-primary rounded-none focus:outline-none ${className}`}
      data-testid={testid}
    />
  );
}

function CellNumber({ value, onCommit, step = 1, testid, allowEmpty = false, placeholder }) {
  const [v, setV] = useState(value === "" || value == null ? "" : String(value));
  const skipCommit = useRef(false);
  useEffect(() => {
    setV(value === "" || value == null ? "" : String(value));
  }, [value]);
  const commit = () => {
    if (skipCommit.current) {
      skipCommit.current = false;
      setV(value === "" || value == null ? "" : String(value));
      return;
    }
    if (v === "" && allowEmpty) {
      if (value !== "" && value != null && value !== 0) onCommit("");
      return;
    }
    const num = v === "" ? 0 : Number(v);
    if (Number.isNaN(num)) { setV(String(value ?? "")); return; }
    if (num !== (Number(value) || 0)) onCommit(num);
  };
  return (
    <input
      type="number"
      min={0}
      step={step}
      value={v}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.target.blur(); }
        if (e.key === "Escape") {
          skipCommit.current = true;
          setV(String(value ?? ""));
          e.target.blur();
        }
      }}
      className="w-full h-7 px-1 text-[11px] text-right font-mono bg-transparent border border-transparent hover:border-border focus:border-primary rounded-none focus:outline-none"
      data-testid={testid}
    />
  );
}
